import React, { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocalStorage } from "usehooks-ts";
import { Scrollbars } from "react-custom-scrollbars";
import { useSelector } from "react-redux";
import format from "date-fns/format";
import { endOfDay, startOfDay } from "date-fns";
import arePropsEqual from "react-fast-compare";
import {
  Box,
  SelectChangeEvent,
  useMediaQuery,
  TextField,
} from "@mui/material";
import Button from "../../../../../../../UI/Buttons/Button";

//styles
import { useStyles } from "./styles";

//types
import { ISubAffiliateReportV2FilterDataType as IFilterData } from "../../../../types";

//utils
import { getDataIds, getGroupByOptions, getPeriod, PeriodTypes } from "../../../../../utils";

//selectors
import {
  selectDealsData,
  selectOptionsData,
  selectPlatformsData
} from "../../../../../../../../store/admin/reports/sub_affiliate_report_v2/selectors";

//hooks
import { usePermissions } from "../../../../../../../../store/common/configuration/usePermissions";
import { useRegistration } from "../../../../../../../../store/common/registration/useRegistration";

//context
import { GlobalContext } from "../../../../../../../../context/GlobalContext";

//constants
import { fullPeriodOptions } from "../../../../../constant";
import { favoriteFilterKeys, reportStorageProps } from "../../../../constants";

//validation
import { validationTextField as validationTextFieldMax } from "../../../../../../../validation/textFieldMax.validation";

//components
import CSelect from "../../../../../../../UI/Fields/CSelect";
import GroupByField, { IItemOption } from "../../../GroupByField";
import CDateRangePicker from "../../../../../../../UI/Fields/CDateRangePicker";
import FavoriteCheckboxField from "../../../../../../../UI/Fields/FavoriteCheckboxField";
import MuiAutocompleteSelectLazy from "../../../../../../../UI/Fields/MuiAutocompleteSelectLazy";
import MuiTwiceDateRangePicker from "../../../../../../../UI/Fields/MuiTwiceDateRangePicker";
import SaveFilterForm from "../../../../../../../common/FiltersComponents/SaveFilterForm";
import { initialSubAffiliateReportV2FilterData } from "../../../DesktopReport";
import MuiAutocompleteSelect from "../../../../../../../UI/Fields/MuiAutocompleteSelect";
import MuiChipsInputField from "../../../../../../../UI/Fields/MuiChipsInput";
import {
  useSubAffiliateReportV2
} from "../../../../../../../../store/admin/reports/sub_affiliate_report_v2/useSubAffiliateReportV2";


interface IFilterProps {
  initialFilter: IFilterData;
  activeField: string;
  setUpdateFilter: (data: any) => void;
  setToggleDrawer: (data: boolean) => void;
  setGenerateReport: React.Dispatch<React.SetStateAction<boolean>>;
  editMode?: boolean;
  filterName?: string;
  onSave?: (data: object) => void;
  setSelectedFilterId?: React.Dispatch<React.SetStateAction<string>>;
  apiV2: boolean;
}

const Filter: React.FC<IFilterProps> = ({
  initialFilter,
  activeField,
  setUpdateFilter,
  setToggleDrawer,
  setGenerateReport,
  editMode = false,
  filterName = '',
  onSave,
  setSelectedFilterId,
  apiV2 = false,
}): JSX.Element => {
  const { t, i18n } = useTranslation();
  const styles = useStyles();
  const mobile = useMediaQuery('(max-width:767px)');
  const { hasPermissions } = usePermissions();

  const hasMyFilters = hasPermissions(["api3.admin.subaffiliatereportcontroller.report_generate"]);

  const context = useContext(GlobalContext);

  const {
	  getFiltersField,
  } = useSubAffiliateReportV2();
	const { countries } = useRegistration();

	const optionsData = useSelector(selectOptionsData);

  //state
  const [filter, setFilter] = useState<IFilterData>(editMode ? initialFilter : context.subAffiliateReportV2Filter);
  const [fieldName, setFieldName] = useState<string>(filterName);

	const [dataCountries, setDataCountries] = useState<any | null>(null);

  const [selectedPeriod, setSelectedPeriod] = useState<PeriodTypes>(initialFilter.period);
  const [startDate, setStartDate] = useState<Date | ''>(new Date(initialFilter.sent_from));
  const [endDate, setEndDate] = useState<Date | ''>(new Date(initialFilter.sent_to));

	const [groupBy, setGroupBy] = useState<IItemOption[]>(editMode ? initialFilter.group_by.fields : context.subAffiliateReportV2Filter.group_by.fields);

  //favorite fields state
	const [favAdmSARV2WhoEarnedIds, setFavAdmSARV2WhoEarnedIds] = useLocalStorage<boolean>(favoriteFilterKeys.whoEarnedIds, false);
	const [favAdmSARV2ReferredAffiliateIds, setFavAdmSARV2ReferredAffiliateIds] = useLocalStorage<boolean>(favoriteFilterKeys.referredAffiliateIds, false);
  const [favAdmSARV2CommissionLevels, setFavAdmSARV2CommissionLevels] = useLocalStorage<boolean>(favoriteFilterKeys.commissionLevels, false);
  const [favAdmSARV2Deals, setFavAdmSARV2Deals] = useLocalStorage<boolean>(favoriteFilterKeys.deals, false);
  const [favAdmSARV2PlayerIds, setFavAdmSARV2PlayerIds] = useLocalStorage<boolean>(favoriteFilterKeys.playerIds, false);
  const [favAdmSARV2Brands, setFavAdmSARV2Brands] = useLocalStorage<boolean>(favoriteFilterKeys.brands, false);
  const [favAdmSARV2Currencies, setFavAdmSARV2Currencies] = useLocalStorage<boolean>(favoriteFilterKeys.currencies, false);
  const [favAdmSARV2PlatformIds, setFavAdmSARV2PlatformIds] = useLocalStorage<boolean>(favoriteFilterKeys.platformIds, false);
  const [favAdmSARV2ProductIds, setFavAdmSARV2ProductIds] = useLocalStorage<boolean>(favoriteFilterKeys.productIds, false);
	const [favAdmSARV2Geo, setFavAdmSARV2Geo] = useLocalStorage<boolean>(favoriteFilterKeys.geo, false);

  useEffect(() => {
    if (!!selectedPeriod) {
      const period = getPeriod(selectedPeriod);

      if (!!period.start && !!period.end) {
        setStartDate(period.start);
        setEndDate(period.end);
        setFilter((prevState: any) => {
          return {
            ...prevState,
            period: selectedPeriod,
            sent_from: format(startOfDay(new Date(period.start)), "yyyy-MM-dd HH:mm:ss"),
            sent_to: format(endOfDay(new Date(period.end)), "yyyy-MM-dd HH:mm:ss"),
          }
        })
      }
    }
  }, [selectedPeriod]);

  useEffect(() => {
    setSelectedPeriod(filter.period);
  }, [filter.period]);

  useEffect(() => {
    if (!editMode) {
      context.setSubAffiliateReportV2Filter(filter);
    }
  }, [filter]);

  useEffect(() => {
    setFilter((prevState) => {
      return {
        ...prevState,
        group_by: {
          fields: groupBy,
        },
      }
    })
  }, [groupBy]);

	useEffect(() => {
		if (!!countries.data) {
			const data = countries.data.reduce((acc: any[], item: any) => {
				acc.push({
					id: item.code,
					value: item.code,
				});
				return acc;
			}, []);
			setDataCountries({
				list: data,
				total_entries: data.length,
			});
		}
	}, [countries, i18n.language]);


	const handleChangeDates = (start: Date, end: Date) => {
    const noEndDate = !end;

    setStartDate(start);
    setEndDate(noEndDate ? start : end);
    setFilter((prevState: any) => {
      return {
        ...prevState,
        period: '',
        sent_from: format(startOfDay(new Date(start)), "yyyy-MM-dd HH:mm:ss"),
        sent_to: format(endOfDay(new Date(noEndDate ? start : end)), "yyyy-MM-dd HH:mm:ss"),
      }
    });
  };

	const handleChangePeriodSelect = (event: SelectChangeEvent) => {
    //@ts-ignore
    const value: PeriodTypes = event.target.value;
    setSelectedPeriod(value);
  };

  const handleApplyFilter = useCallback(() => {
    setUpdateFilter(filter);
    setToggleDrawer(false);

    if (!editMode) {
      if (typeof setSelectedFilterId === 'function') {
        setSelectedFilterId('');
      }
      setTimeout(() => {
        setGenerateReport((prevState) => {
          return !prevState
        });
      }, 120);
    }
  }, [filter]);

  const fieldPayload = () => {
    return {
      page: 1,
      search: null,
      perPage: 100,
    }
  };

  const fieldCurrenciesPayload = () => {
    return {
      page: 1,
      search: null,
      perPage: 100,
      brandCodes: !!filter.brands.length ? getDataIds(filter.brands) : null,
    }
  };

  const validateName = validationTextFieldMax(fieldName, 255, {
    required: t("common.validation.required_field"),
    maxLength: t("common.validation.max_length"),
  });

  const handleSave = useCallback(() => {
    if (typeof onSave === 'function') {
      onSave({
        name: fieldName,
        filter: filter,
      })
    }
  }, [filter, fieldName]);

  return (<>
    {!!optionsData && !!dataCountries && (<>
      <Box className={styles.wrapper}>
        <Scrollbars
          className={styles.customScroll}
          style={{ overflow: "hidden" }}
          hideTracksWhenNotNeeded={true}
          renderView={props => <div {...props} className="view"/>}
          renderTrackHorizontal={props => <div {...props} className="track-horizontal"/>}
          renderTrackVertical={props => <div {...props} className="track-vertical"/>}
        >
          {editMode && (
            <Box className={styles.box}>
              <TextField
                className={styles.fieldFull}
                size="small"
                id="filter_name"
                name="filter_name"
                type="text"
                label={t(`common.forms.fields.filter_name`)}
                value={fieldName}
                error={!!validateName}
                helperText={!!validateName ? validateName : ''}
                onChange={(event) => {
                  setFieldName(event.target.value);
                }}
              />
            </Box>
          )}
          {(<>
            {mobile && (<>
              <Box className={styles.box}>
                <CSelect
                  name="period"
                  label={t("admin.reports.filter.period")}
                  value={selectedPeriod}
                  options={fullPeriodOptions}
                  onChange={handleChangePeriodSelect}
                  prefix="common.components.periods."
                />
              </Box>

              <Box className={`${styles.box} noMargin`}>
                <CDateRangePicker
                  startDate={startDate}
                  endDate={endDate}
                  onChange={handleChangeDates}
                />
              </Box>

              <Box className={`${styles.box} noMargin`}>
                <GroupByField
                  name="groupBy"
                  value={groupBy}
                  options={getGroupByOptions(optionsData.groupBy)}
                  setFilter={setGroupBy}
                  prefix={"admin.sub_affiliate_report_v2.filter.group_by_select."}
                />
              </Box>
            </>)}
	          {/*WhoEarned*/}
	          {optionsData.availableFilters.includes("who-earned") && (
		          <Box className={styles.box}>
			          <Box className={styles.fieldWrapper}>
				          {!editMode && !mobile && (
					          <Box className={styles.fieldFavorite}>
						          <FavoriteCheckboxField
							          checked={favAdmSARV2WhoEarnedIds}
							          setChecked={setFavAdmSARV2WhoEarnedIds}
							          fieldName={t(`admin.reports.filter.whoEarnedIds`)}
						          />
					          </Box>
				          )}
				          <Box className={styles.field}>
					          <MuiAutocompleteSelectLazy
						          keyValue="value"
						          keyId="id"
						          id="whoEarnedIds"
						          name="whoEarnedIds"
						          label={t(`admin.reports.filter.whoEarnedIds`)}
						          value={filter.whoEarnedIds}
						          multiple={true}
						          showSelectAll={false}
						          disabled={false}
						          getOptions={getFiltersField}
						          payloadData={fieldPayload()}
						          labelWithId={true}
						          onChange={setFilter}
						          disableCloseOnSelect={true}
						          autoFocus={activeField === 'whoEarnedIds'}
						          showLabelCounter={true}
						          showNameInPayload={true}
					          />
				          </Box>
			          </Box>
		          </Box>
	          )}
            {/*referred-affiliates*/}
	          {optionsData.availableFilters.includes("referred-affiliates") && (
		          <Box className={styles.box}>
			          <Box className={styles.fieldWrapper}>
				          {!editMode && !mobile && (
					          <Box className={styles.fieldFavorite}>
						          <FavoriteCheckboxField
							          checked={favAdmSARV2ReferredAffiliateIds}
							          setChecked={setFavAdmSARV2ReferredAffiliateIds}
							          fieldName={t(`admin.reports.filter.referredAffiliateIds`)}
						          />
					          </Box>
				          )}
				          <Box className={styles.field}>
					          <MuiAutocompleteSelectLazy
						          keyValue="value"
						          keyId="id"
						          id="referredAffiliateIds"
						          name="referredAffiliateIds"
						          label={t(`admin.reports.filter.referredAffiliateIds`)}
						          value={filter.referredAffiliateIds}
						          multiple={true}
						          showSelectAll={false}
						          disabled={false}
						          getOptions={getFiltersField}
						          payloadData={fieldPayload()}
						          labelWithId={true}
						          onChange={setFilter}
						          disableCloseOnSelect={true}
						          autoFocus={activeField === 'referredAffiliateIds'}
						          showLabelCounter={true}
						          showNameInPayload={true}
					          />
				          </Box>
			          </Box>
		          </Box>
	          )}
            {/*commission-levels*/}
	          {optionsData.availableFilters.includes("commission-levels") && (
		          <Box className={styles.box}>
			          <Box className={styles.fieldWrapper}>
				          {!editMode && !mobile && (
					          <Box className={styles.fieldFavorite}>
						          <FavoriteCheckboxField
							          checked={favAdmSARV2CommissionLevels}
							          setChecked={setFavAdmSARV2CommissionLevels}
							          fieldName={t(`admin.reports.filter.commissionLevels`)}
						          />
					          </Box>
				          )}
				          <Box className={styles.field}>
					          <MuiAutocompleteSelectLazy
						          keyValue="value"
						          keyId="id"
						          id="commissionLevels"
						          name="commissionLevels"
						          label={t(`admin.reports.filter.commissionLevels`)}
						          value={filter.commissionLevels}
						          multiple={true}
						          showSelectAll={false}
						          disabled={false}
						          getOptions={getFiltersField}
						          payloadData={fieldPayload()}
						          labelWithId={false}
						          onChange={setFilter}
						          disableCloseOnSelect={true}
						          autoFocus={activeField === 'commissionLevels'}
						          showLabelCounter={true}
						          showNameInPayload={true}
					          />
				          </Box>
			          </Box>
		          </Box>
	          )}
            {/*deals*/}
	          {optionsData.availableFilters.includes("deals") && (
		          <Box className={styles.box}>
			          <Box className={styles.fieldWrapper}>
				          {!editMode && !mobile && (
					          <Box className={styles.fieldFavorite}>
						          <FavoriteCheckboxField
							          checked={favAdmSARV2Deals}
							          setChecked={setFavAdmSARV2Deals}
							          fieldName={t(`admin.reports.filter.deals`)}
						          />
					          </Box>
				          )}
				          <Box className={styles.field}>
					          <MuiAutocompleteSelectLazy
						          keyValue="value"
						          keyId="id"
						          id="deals"
						          name="deals"
						          label={t(`admin.reports.filter.deals`)}
						          value={filter.deals}
						          multiple={true}
						          showSelectAll={false}
						          disabled={false}
						          getOptions={getFiltersField}
						          payloadData={fieldPayload()}
						          labelWithId={true}
						          onChange={setFilter}
						          disableCloseOnSelect={true}
						          autoFocus={activeField === 'deals'}
						          showLabelCounter={true}
						          showNameInPayload={true}
					          />
				          </Box>
			          </Box>
		          </Box>
	          )}
            {/*players*/}
	          {optionsData.availableFilters.includes("players") && (
		          <Box className={styles.box}>
			          <Box className={styles.fieldWrapper}>
				          {!editMode && !mobile && (
					          <Box className={styles.fieldFavorite}>
						          <FavoriteCheckboxField
							          checked={favAdmSARV2PlayerIds}
							          setChecked={setFavAdmSARV2PlayerIds}
							          fieldName={t(`admin.reports.filter.players`)}
						          />
					          </Box>
				          )}
				          <Box className={styles.field}>
					          <MuiChipsInputField
						          id="players"
						          name="players"
						          label={t(`admin.reports.filter.players`)}
						          value={filter.players}
						          onChange={setFilter}
						          autoFocus={activeField === 'players'}
					          />
				          </Box>
			          </Box>
		          </Box>
	          )}
            {/*brands*/}
            {optionsData.availableFilters.includes("brands") && (
              <Box className={styles.box}>
                <Box className={styles.fieldWrapper}>
                  {!editMode && !mobile && (
                    <Box className={styles.fieldFavorite}>
                      <FavoriteCheckboxField
                        checked={favAdmSARV2Brands}
                        setChecked={setFavAdmSARV2Brands}
                        fieldName={t(`admin.reports.filter.brands`)}
                      />
                    </Box>
                  )}
                  <Box className={styles.field}>
                    <MuiAutocompleteSelectLazy
                      keyValue="value"
                      keyId="id"
                      id="brands"
                      name="brands"
                      label={t(`admin.reports.filter.brands`)}
                      value={filter.brands}
                      multiple={true}
                      showSelectAll={false}
                      disabled={false}
                      getOptions={getFiltersField}
                      payloadData={fieldPayload()}
                      labelWithId={true}
                      onChange={setFilter}
                      disableCloseOnSelect={true}
                      autoFocus={activeField === 'brands'}
                      showLabelCounter={true}
                      showNameInPayload={true}
                    />
                  </Box>
                </Box>
              </Box>
            )}
            {/*currencies*/}
            {optionsData.availableFilters.includes("currencies") && (
              <Box className={styles.box}>
                <Box className={styles.fieldWrapper}>
                  {!editMode && !mobile && (
                    <Box className={styles.fieldFavorite}>
                      <FavoriteCheckboxField
                        checked={favAdmSARV2Currencies}
                        setChecked={setFavAdmSARV2Currencies}
                        fieldName={t(`admin.reports.filter.currencies`)}
                      />
                    </Box>
                  )}
                  <Box className={styles.field}>
                    <MuiAutocompleteSelectLazy
                      keyValue="value"
                      keyId="id"
                      id="currencies"
                      name="currencies"
                      label={t(`admin.reports.filter.currencies`)}
                      value={filter.currencies}
                      multiple={true}
                      showSelectAll={false}
                      disabled={false}
                      getOptions={getFiltersField}
                      payloadData={fieldCurrenciesPayload()}
                      labelWithId={false}
                      onChange={setFilter}
                      disableCloseOnSelect={true}
                      autoFocus={activeField === 'currencies'}
                      showLabelCounter={true}
                      showNameInPayload={true}
                    />
                  </Box>
                </Box>
              </Box>
            )}
	          {/*platformIds*/}
	          {optionsData.availableFilters.includes("platformIds") && (
		          <Box className={styles.box}>
			          <Box className={styles.fieldWrapper}>
				          {!editMode && !mobile && (
					          <Box className={styles.fieldFavorite}>
						          <FavoriteCheckboxField
							          checked={favAdmSARV2PlatformIds}
							          setChecked={setFavAdmSARV2PlatformIds}
							          fieldName={t(`admin.reports.filter.platformIds`)}
						          />
					          </Box>
				          )}
				          <Box className={styles.field}>
					          <MuiAutocompleteSelectLazy
						          keyValue="value"
						          keyId="id"
						          id="platformIds"
						          name="platformIds"
						          label={t(`admin.reports.filter.platformIds`)}
						          value={filter.platformIds}
						          multiple={true}
						          showSelectAll={false}
						          disabled={false}
						          getOptions={getFiltersField}
						          payloadData={fieldPayload()}
						          labelWithId={false}
						          onChange={setFilter}
						          disableCloseOnSelect={true}
						          autoFocus={activeField === 'platformIds'}
						          showLabelCounter={true}
						          showNameInPayload={true}
					          />
				          </Box>
			          </Box>
		          </Box>
	          )}
	          {/*productIds*/}
	          {optionsData.availableFilters.includes("products") && (
		          <Box className={styles.box}>
			          <Box className={styles.fieldWrapper}>
				          {!editMode && !mobile && (
					          <Box className={styles.fieldFavorite}>
						          <FavoriteCheckboxField
							          checked={favAdmSARV2ProductIds}
							          setChecked={setFavAdmSARV2ProductIds}
							          fieldName={t(`admin.reports.filter.productIds`)}
						          />
					          </Box>
				          )}
				          <Box className={styles.field}>
					          <MuiAutocompleteSelectLazy
						          keyValue="value"
						          keyId="id"
						          id="productIds"
						          name="productIds"
						          label={t(`admin.reports.filter.productIds`)}
						          value={filter.productIds}
						          multiple={true}
						          showSelectAll={false}
						          disabled={false}
						          getOptions={getFiltersField}
						          payloadData={fieldPayload()}
						          labelWithId={false}
						          onChange={setFilter}
						          disableCloseOnSelect={true}
						          autoFocus={activeField === 'productIds'}
						          showLabelCounter={true}
						          showNameInPayload={true}
					          />
				          </Box>
			          </Box>
		          </Box>
	          )}
	          {/*GEO*/}
	          {optionsData.availableFilters.includes("geo") && apiV2 && (<>
		          <Box className={styles.box}>
			          <Box className={styles.fieldWrapper}>
				          {!editMode && !mobile && (
					          <Box className={styles.fieldFavorite}>
						          <FavoriteCheckboxField
							          checked={favAdmSARV2Geo}
							          setChecked={setFavAdmSARV2Geo}
							          fieldName={t(`admin.reports.filter.geo`)}
						          />
					          </Box>
				          )}
				          <Box className={styles.field}>
					          <MuiAutocompleteSelect
						          keyValue="value"
						          keyId="id"
						          id="geo"
						          name="geo"
						          label={t(`admin.reports.filter.geo`)}
						          value={filter.geo}
						          multiple={true}
						          showSelectAll={false}
						          disabled={false}
						          staticOptions={dataCountries.list}
						          onChange={setFilter}
						          disableCloseOnSelect={true}
						          autoFocus={activeField === 'geo'}
						          showLabelCounter={true}
						          prefix={`common.components.creative.list_geolocales.`}
					          />
				          </Box>
			          </Box>
		          </Box>
	          </>)}

            {!editMode && hasMyFilters && (
              <SaveFilterForm
                filter={{
                  ...filter,
                }}
                type={reportStorageProps.type}
                target={reportStorageProps.target}
                exceptions={reportStorageProps.exceptions}
                exceptionsIds={reportStorageProps.exceptionsIds}
                disabled={
                  arePropsEqual(
                    {
                      ...initialSubAffiliateReportV2FilterData,
                      sent_from: format(new Date(initialSubAffiliateReportV2FilterData.sent_from), "yyyy-MM-dd HH:mm:ss"),
                      sent_to: format(new Date(initialSubAffiliateReportV2FilterData.sent_to), "yyyy-MM-dd HH:mm:ss"),
                    },
                    {
                      ...filter,
                      sent_from: format(new Date(filter.sent_from), "yyyy-MM-dd HH:mm:ss"),
                      sent_to: format(new Date(filter.sent_to), "yyyy-MM-dd HH:mm:ss"),
                    }
                  )
                }
              />
            )}
          </>)}
        </Scrollbars>
      </Box>
      <Box className={styles.actions}>
        <Box className={styles.actionsWrapper}>
          {editMode ? (
            <Button
              gradient
              className={styles.button}
              fullWidth
              disableElevation
              type="submit"
              variant="contained"
              onClick={handleSave}
              disabled={
                arePropsEqual({
                  name: filterName,
                  ...initialFilter,
                  sent_from: format(new Date(initialFilter.sent_from), "yyyy-MM-dd HH:mm:ss"),
                  sent_to: format(new Date(initialFilter.sent_to), "yyyy-MM-dd HH:mm:ss"),
                }, {
                  name: fieldName,
                  ...filter,
                  sent_from: format(new Date(filter.sent_from), "yyyy-MM-dd HH:mm:ss"),
                  sent_to: format(new Date(filter.sent_to), "yyyy-MM-dd HH:mm:ss"),
                }) || !!validateName
              }
            >
              {t("common.buttons.save")}
            </Button>
          ) : (
            <Button
              gradient
              className={styles.button}
              fullWidth
              disableElevation
              type="submit"
              variant="contained"
              onClick={handleApplyFilter}
              disabled={arePropsEqual(initialFilter, filter)}
            >
              {t("common.buttons.apply")}
            </Button>
          )}
          <Button
            className={styles.button}
            color="secondary"
            disableElevation
            type="button"
            variant="outlined"
            onClick={() => setToggleDrawer(false)}
          >
            {t("common.buttons.cancel")}
          </Button>
        </Box>
      </Box>
    </>)}
  </>);
};

export default Filter;
